import { components, PublicOfferV2 } from '@luxuryescapes/contract-public-offer'
import { Tour as TourAPI } from '@luxuryescapes/contract-svc-tour'
import { mapObject } from 'lib/objectUtils'
import { TOUR_V2_LE_SOURCE, TOUR_V2_PACKAGE_OPTION_BASE } from 'constants/tours'
import { sortBy, unique } from 'lib/array/arrayUtils'

// pick only the date portion of the date string (no time + timezone)
export function extractAbsoluteDate(dateString: string) {
  return dateString.substring(0, 10)
}

export function mapTourV2OfferType(
  source: string,
  brand: string,
): Tours.V2OfferType {
  if (source === TOUR_V2_LE_SOURCE) {
    if (brand === 'luxuryescapes') {
      return 'direct_tour'
    }
    return 'partner_tour'
  } else {
    return 'connection_tour'
  }
}

const decadeRegions = new Set(['AU', 'NZ'])

function mapUrgencyTags(offer: PublicOfferV2.TourV2Offer, regionCode: string) {
  const tags = offer.urgencyTags ?? []
  if (offer.campaignTags.includes('BODAU') && decadeRegions.has(regionCode)) {
    tags.push('bestofthedecade')
  } else if (offer.campaignTags.includes('BODINT')) {
    tags.push('bestof2023')
  }

  return tags
}

export function tourV2OfferMap(
  offer: PublicOfferV2.TourV2Offer,
  regionCode: string,
): Tours.V2Offer {
  const mappedOffer: Tours.V2Offer = {
    analyticsType: 'tourV2',
    productType: offer.productType,
    brand: tourV2BrandMap(offer.brandObject),
    departures: mapObject(offer.departures, (departure, departureKey) => tourV2DepartureMap(departure, departureKey as string, offer.tourOptions[departure.fkTourOptionId])),
    finePrint: tourV2FinePrintMap(offer.finePrint),
    id: offer.id,
    name: offer.name,
    parentType: 'tour_v2',
    purchasableOptions: offer.options.map(option => tourV2PurchasableOptionMap(option, offer.tourOptions[option.fkTourOptionId])),
    roomTypePricing: mapObject(offer.roomTypePricing, tourV2RoomTypePriceMap),
    saleUnit: 'room',
    slug: offer.slug,
    type: mapTourV2OfferType(offer.source, offer.brand),
    variations: mapTourV2OfferVariations(offer.tourOptions, offer.departures),
    urgencyTags: mapUrgencyTags(offer, regionCode),
    showPricePerDay: offer.showPricePerDay,
    activityLevel: offer.activityLevel,
    privateRequestKey: extractTourV2DeparturePrivateRequestKey(offer.departures),
    source: offer.source,
    isLeTour: (offer.source === TOUR_V2_LE_SOURCE),
    recommendedHotelAddOns: offer.recommendedHotelAddOns,
    reviewsRating: offer.reviewsRating || 0,
    reviewsTotal: offer.reviewsTotal || 0,
    reviewsSource: offer.reviewsSource || 'luxuryescapes',
    video: offer.video ?? undefined,
  }

  if (typeof offer.depositAmount !== 'undefined' && typeof offer.depositType !== 'undefined') {
    mappedOffer.depositAmount = tourV2DepositAmountMap(offer.depositAmount ?? 0, offer.depositType)
    mappedOffer.depositType = offer.depositType
  }

  if (offer.faq) {
    mappedOffer.faq = offer.faq.map(tourV2FAQMap)
  }

  return mappedOffer
}

export function tourV2OfferSummaryMap(
  offer: PublicOfferV2.TourV2OfferSummary,
  regionCode: string,
): Tours.V2OfferSummary {
  return {
    analyticsType: 'tourV2',
    productType: offer.productType,
    allVariationsCount: offer.tourOptionCount,
    brand: tourV2BrandMap(offer.brandObject),
    departures: mapObject(offer.departures, (departure, departureKey) => tourV2DepartureMap(departure, departureKey as string, offer.tourOptions[departure.fkTourOptionId])),
    id: offer.id,
    summaryId: `${offer.id}__${Object.keys(offer.tourOptions)[0]}`,
    name: offer.name,
    parentType: 'tour_v2',
    purchasableOptions: offer.options.map(option => tourV2PurchasableOptionMap(option, offer.tourOptions[option.fkTourOptionId])),
    roomTypePricing: mapObject(offer.roomTypePricing, tourV2RoomTypePriceMap),
    saleUnit: 'room',
    slug: offer.slug,
    type: mapTourV2OfferType(offer.source, offer.brand),
    variations: mapTourV2OfferVariations(offer.tourOptions, offer.departures),
    urgencyTags: mapUrgencyTags(offer as any, regionCode),
    showPricePerDay: offer.showPricePerDay,
    depositType: offer.depositType,
    depositAmount: tourV2DepositAmountMap(offer.depositAmount ?? 0, offer.depositType),
    source: offer.source,
    isLeTour: (offer.source === TOUR_V2_LE_SOURCE),
    recommendedHotelAddOns: offer.recommendedHotelAddOns,
    reviewsRating: offer.reviewsRating || 0,
    reviewsTotal: offer.reviewsTotal || 0,
    reviewsSource: offer.reviewsSource || 'luxuryescapes',
  }
}

export function tourV2BrandMap(
  brandObject: PublicOfferV2.Brand,
): Tours.V2OfferBrand {
  const {
    code,
    name,
    svcLogoId,
  } = brandObject

  const brand: Tours.V2OfferBrand = {
    code,
    name,
  }

  if (svcLogoId) {
    brand.logoImage = { id: svcLogoId, title: `${name} logo` }
  }

  return brand
}

export function tourV2DepartureMap(
  departure: PublicOfferV2.Departure,
  departureKey: string,
  tourOption: PublicOfferV2.TourOption,
): Tours.V2OfferDeparture {
  const mappedDeparture: Tours.V2OfferDeparture = {
    endDate: extractAbsoluteDate(departure.endDate),
    fkSeasonId: departure.fkSeasonId,
    fkVariationId: getTourV2VariationId(tourOption),
    id: departureKey,
    isGuaranteed: departure.definiteDeparture,
    startDate: extractAbsoluteDate(departure.startDate),
    status: departure.availability.status,
    urgencyTags: departure.urgencyTags ?? [],
    privateRequestKey: departure.privateRequestKey,
  }

  if (departure.currencyCode) {
    mappedDeparture.currencyCode = departure.currencyCode
  }
  if (departure.startTimeLocal) {
    mappedDeparture.startTimeLocal = departure.startTimeLocal
  }
  if (departure.endTimeLocal) {
    mappedDeparture.endTimeLocal = departure.endTimeLocal
  }
  if (departure.groupSize) {
    mappedDeparture.groupSize = departure.groupSize
  }

  return mappedDeparture
}

function tourV2FinePrintSectionMap(
  finePrintItem: TourAPI.FinePrintItem,
): Tours.V2OfferFinePrintSection {
  return {
    category: finePrintItem.category,
    title: finePrintItem.title,
    body: finePrintItem.body,
  }
}

export function tourV2FinePrintMap(
  finePrint: TourAPI.FinePrint,
): Tours.V2OfferFinePrint {
  return {
    sections: finePrint.items?.map(tourV2FinePrintSectionMap) ?? [],
  }
}

export function tourV2PurchasableOptionMap(
  purchasableOption: PublicOfferV2.PurchasableOption,
  tourOption: PublicOfferV2.TourOption,
): Tours.V2OfferPurchasableOption {
  const mappedPurchasableOption: Tours.V2OfferPurchasableOption = {
    fkDepartureId: purchasableOption.fkDepartureId,
    fkRoomTypePricingId: purchasableOption.fkRoomTypePricingId,
    fkSeasonId: purchasableOption.fkSeasonId,
    fkTourId: purchasableOption.fkTourId,
    fkVariationId: getTourV2VariationId(tourOption),
    fullPrice: purchasableOption.fullPrice,
    price: purchasableOption.price,
    roomType: purchasableOption.roomType as Tours.V2OfferRoomType, // TODO: this could be a type mapper. needs fallback
  }

  if (purchasableOption.valuedAtPrice) {
    mappedPurchasableOption.valuedAt = purchasableOption.valuedAtPrice
  }

  return mappedPurchasableOption
}

export function tourV2RoomTypePriceMap(
  roomTypePrice: PublicOfferV2.RoomTypePrice,
): Tours.V2OfferRoomTypePrice {
  const mappedRoomTypePrice: Tours.V2OfferRoomTypePrice = {
    id: roomTypePrice.id,
    maxChildDiscounts: roomTypePrice.maxChildDiscounts || 0,
  }

  if (roomTypePrice.childPrice >= 0) {
    mappedRoomTypePrice.childPrice = roomTypePrice.childPrice
  }

  return mappedRoomTypePrice
}

export function tourV2InclusionMap(
  inclusion: TourAPI.Inclusion,
): Tours.V2OfferInclusion {
  return {
    title: inclusion.title,
    body: inclusion.body,
  }
}

export function tourV2ImageMap(
  image: PublicOfferV2.Image,
): App.Image {
  return {
    id: image.id,
    title: image.title,
  }
}

export function tourV2ItineraryItemMap(
  itineraryItem: PublicOfferV2.ItineraryItem,
): Tours.V2OfferItineraryItem {
  const mappedItineraryItem: Tours.V2OfferItineraryItem = {
    description: itineraryItem.description,
    duration: itineraryItem.duration,
    locationsVisited: itineraryItem.locationsVisited ?? [],
    meals: itineraryItem.meals ?? [],
    startDay: itineraryItem.startDay,
    title: itineraryItem.title,
    locationsVisitedDetails: itineraryItem.locationsVisitedDetails ?? [],
  }

  if (itineraryItem.accommodation) {
    mappedItineraryItem.accommodation = itineraryItem.accommodation
  }
  if (itineraryItem.region) {
    mappedItineraryItem.region = itineraryItem.region
  }

  return mappedItineraryItem
}

export function tourV2ReservationSnapshotItineraryItemMap(
  itineraryItem: TourAPI.SnapshotItineraryItem,
): Tours.V2OfferItineraryItem {
  const mappedItineraryItem: Tours.V2OfferItineraryItem = {
    description: itineraryItem.description,
    duration: itineraryItem.duration,
    locationsVisited: itineraryItem.locationsVisited ?? [],
    meals: itineraryItem.meals ?? [],
    startDay: itineraryItem.startDay,
    title: itineraryItem.title,
    locationsVisitedDetails: [],
  }

  if (itineraryItem.accommodation) {
    mappedItineraryItem.accommodation = itineraryItem.accommodation
  }
  if (itineraryItem.region) {
    mappedItineraryItem.region = itineraryItem.region
  }

  return mappedItineraryItem
}

function getTourV2VariationId(tourOption: PublicOfferV2.TourOption) {
  return tourOption.slug ?? tourOption.id
}

export function mapTourV2OfferVariations(
  tourOptions: { [key: string]: components['schemas']['v2TourOption'] },
  departures: { [key: string]: components['schemas']['v2TourDeparture'] },
): Tours.V2Offer['variations'] {
  const mappedOfferVariations: Tours.V2Offer['variations'] = {}
  const numberDeparturesByVariation = getDeparturesAvailableByVariationId(departures)
  const tourOptionsAsArray = Object.values(tourOptions)
  const baseTourOption = tourOptionsAsArray.find((tourOption) => tourOption.sourceTourOptionName === TOUR_V2_PACKAGE_OPTION_BASE)
  for (const tourOption of tourOptionsAsArray) {
    if ((numberDeparturesByVariation?.get(tourOption.id) ?? 0) > 0) {
      const baseTourOptionToCompare = !(tourOption.sourceTourOptionName === TOUR_V2_PACKAGE_OPTION_BASE) ? baseTourOption : null
      mappedOfferVariations[getTourV2VariationId(tourOption)] = tourV2VariationMap(tourOption, baseTourOptionToCompare)
    }
  }
  return mappedOfferVariations
}

function tourV2VariationMap(
  tourOption: PublicOfferV2.TourOption,
  baseTourOption: PublicOfferV2.TourOption | null,
): Tours.V2OfferVariation {
  const itineraryDestinations = (baseTourOption) ?
    itineraryDiffDestinationsVisited(tourOption.defaultSeason.itinerary, baseTourOption.defaultSeason.itinerary) :
    extractItinerariesLocations(tourOption.defaultSeason.itinerary)
  const mappedVariation: Tours.V2OfferVariation = {
    countriesVisited: tourOption.defaultSeason.countriesVisited ?? [],
    description: tourOption.defaultSeason.copy.description,
    diningInclusions: tourOption.defaultSeason.copy.diningInclusions?.map(tourV2InclusionMap) ?? [],
    endLocation: tourOption.defaultSeason.endLocation,
    fkSeasonId: tourOption.defaultSeason.id,
    fkTourOptionId: tourOption.id,
    id: getTourV2VariationId(tourOption),
    images: tourOption.defaultSeason.images.map(tourV2ImageMap) ?? [],
    itinerary: sortBy(tourOption.defaultSeason.itinerary?.map(tourV2ItineraryItemMap), itinerary => itinerary.startDay, 'asc') ?? [],
    name: tourOption.defaultSeason.name,
    startLocation: tourOption.defaultSeason.startLocation,
    travelInclusions: tourOption.defaultSeason.copy.travelInclusions?.map(tourV2InclusionMap) ?? [],
    isPrivateRequest: tourOption.isPrivateRequest,
    privateRequestFromPrice: tourOption.privateRequestFromPrice,
    maxPax: tourOption.maxPax,
    maxAge: tourOption.maxAge,
    inclusionItems: tourOption.defaultSeason.inclusionItems,
    packageOptionName: tourOption.sourceTourOptionName,
    itineraryDestinations,
    availablePackageUpgrades: tourOption.availablePackageUpgrades,
    baseTourOptionId: baseTourOption ? getTourV2VariationId(baseTourOption) : undefined,
  }

  if (tourOption.defaultSeason.maxChildPriceAge) {
    mappedVariation.maxChildPriceAge = tourOption.defaultSeason.maxChildPriceAge
  }
  if (tourOption.defaultSeason.minChildPriceAge) {
    mappedVariation.minChildPriceAge = tourOption.defaultSeason.minChildPriceAge
  }
  if (tourOption.defaultSeason.routeMapImage) {
    mappedVariation.routeMapImage = { id: tourOption.defaultSeason.routeMapImage, title: `${tourOption.defaultSeason.name} route map` }
  }

  return mappedVariation
}

function itineraryDiffDestinationsVisited(currentDestinations: PublicOfferV2.ItineraryItem[], baseDestinations: PublicOfferV2.ItineraryItem[]) {
  const itinerariesA = extractItinerariesLocations(currentDestinations)
  const itinerariesB = extractItinerariesLocations(baseDestinations)
  return itinerariesA.filter(location => !itinerariesB.includes(location))
}

function extractItinerariesLocations(itineraryItems: PublicOfferV2.ItineraryItem[]) {
  return unique(
    itineraryItems
      .flatMap(itinerary => itinerary.locationsVisitedDetails)
      .map(location => location.name),
  )
}

export function tourV2FAQMap(
  faq: TourAPI.FAQQuestion,
): Tours.V2OfferFrequentlyAskedQuestion {
  return {
    content: faq.content,
    key: faq.key,
    title: faq.title,
  }
}

export function tourV2DepositAmountMap(depositAmount: number, depositType: Tours.V2OfferDepositType) {
  if (depositType === 'percentage') {
    return depositAmount * 100
  }

  if (depositType === 'default') {
    return 0
  }

  return depositAmount
}

export function getDeparturesAvailableByVariationId(
  departures: { [key: string]: components['schemas']['v2TourDeparture'] },
): Map<string, number> | undefined {
  if (!departures || Object.keys(departures).length === 0) {
    return undefined
  }
  return Object.values(departures)
    .filter((departure) => departure.availability.status !== 'hidden')
    .map(departure => departure.fkTourOptionId)
    .reduce((acc, tourOptionId) => acc.set(tourOptionId, (acc.get(tourOptionId) ?? 0) + 1), new Map())
}

export function extractTourV2DeparturePrivateRequestKey(departures: { [key: string]: components['schemas']['v2TourDeparture'] }): string | null {
  const departuresPrivateKeys = Object.values(departures)
    .filter(departure => !!departure.privateRequestKey)
    .map(departure => departure.privateRequestKey)
  return departuresPrivateKeys?.[0] ?? null
}
